import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, Info } from "lucide-react";
import { triggerOnboarding } from "../services/tenant";
import { BILLING_TIERS, type BillingTier } from "../types/tenant";
import { PageHeader } from "../components/layout/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { FIELD_CLS, FIELD_LABEL_CLS, HELP_CLS } from "../components/chrome/fieldChrome";
import { cn } from "../utils/cn";

const TENANT_KEY_REGEX = /^[a-z][a-z0-9-]{2,29}$/;

const TIER_DESC: Record<BillingTier, string> = {
  free: "Sem custo. Limites básicos.",
  starter: "Plano de entrada. Recursos suficientes para tenants pequenos.",
  pro: "Recursos avançados, mais usuários e integrações.",
  enterprise: "Customização total, SLAs garantidos.",
};

const SAGA_STEPS = [
  "idp.createRealm",
  "db.provisionDatabase",
  "dns.createSubdomain",
  "storage.createBucket",
  "branding.publishAssets",
  "notifications.setupTemplates",
  "idp.registerApp",
  "tenant.recordLocal",
];

export default function OnboardingNew() {
  const navigate = useNavigate();
  const [tenantKey, setTenantKey] = useState("");
  const [name, setName] = useState("");
  const [brandingColor, setBrandingColor] = useState("#334155");
  const [billingTier, setBillingTier] = useState<BillingTier>("starter");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!TENANT_KEY_REGEX.test(tenantKey)) return setErr("tenantKey deve ser kebab-case minúsculo (3-30 chars, começar com letra)");
    if (!name.trim()) return setErr("Nome do tenant obrigatório");

    setSubmitting(true);
    try {
      const r = await triggerOnboarding({
        tenantKey: tenantKey.trim(),
        name: name.trim(),
        brandingColor,
        billingTier,
      });
      toast.success(`Tenant '${r.tenantKey}' onboarded — saga ${r.status}`);
      navigate(`/sagas/${r.sagaId}`);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Falha no onboarding";
      setErr(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Novo tenant"
        subtitle={
          <span className="inline-flex items-center gap-1.5">
            Onboarding completo via saga{" "}
            <code className="font-mono-tight text-xs px-1 py-0.5 rounded bg-surface-2">OnboardTenant</code>
            {" · "} 8 steps
          </span>
        }
        actions={
          <Link to="/tenants"><Button size="sm" variant="outline"><ArrowLeft size={14} /> Tenants</Button></Link>
        }
      />

      <div className="p-6">
        <div className="max-w-5xl grid lg:grid-cols-[1.6fr_1fr] gap-5">
          {/* Form principal */}
          <form onSubmit={onSubmit} className="rounded-md border border-border bg-surface shadow-sm divide-y divide-border-subtle">
            <div className="p-5 space-y-4">
              <div className={FIELD_CLS}>
                <Label htmlFor="tenantKey">Tenant key</Label>
                <Input
                  id="tenantKey"
                  value={tenantKey}
                  onChange={(e) => setTenantKey(e.target.value)}
                  placeholder="shopping-x"
                  required
                  autoFocus
                  className="font-mono-tight"
                />
                <span className={HELP_CLS}>
                  Kebab-case minúsculo, 3–30 chars, começa com letra. Vira parte do subdomain
                  (<code className="font-mono-tight">{tenantKey || "<key>"}.wynk.app.br</code>) e do nome do banco.
                </span>
              </div>

              <div className={FIELD_CLS}>
                <Label htmlFor="name">Nome amigável</Label>
                <Input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Shopping X" required />
              </div>
            </div>

            <div className="p-5 space-y-4">
              <div className={FIELD_CLS}>
                <Label htmlFor="brandingColor">Cor primária do tenant</Label>
                <div className="flex items-center gap-2.5">
                  <label
                    htmlFor="brandingColor"
                    className="relative h-9 w-9 rounded-md border border-border-strong cursor-pointer overflow-hidden shadow-sm"
                    style={{ backgroundColor: brandingColor }}
                    title="Abrir seletor de cor"
                  >
                    <input
                      id="brandingColor"
                      type="color"
                      value={brandingColor}
                      onChange={(e) => setBrandingColor(e.target.value)}
                      className="absolute inset-0 opacity-0 cursor-pointer"
                    />
                  </label>
                  <Input
                    value={brandingColor}
                    onChange={(e) => setBrandingColor(e.target.value)}
                    className="font-mono-tight w-28"
                    maxLength={7}
                    aria-label="Cor primária em hex"
                  />
                  <span className="text-xs text-muted-foreground">Aparece no logo e accents do tenant</span>
                </div>
              </div>

              <div className={FIELD_CLS}>
                <span className={FIELD_LABEL_CLS}>Billing tier</span>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {BILLING_TIERS.map((tier) => (
                    <button
                      key={tier}
                      type="button"
                      onClick={() => setBillingTier(tier)}
                      className={cn(
                        "py-2 px-3 rounded-md border text-sm font-medium capitalize transition-all text-left",
                        billingTier === tier
                          ? "border-primary bg-primary-soft text-primary-soft-fg ring-1 ring-primary/30"
                          : "border-border hover:border-border-strong bg-surface text-foreground",
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <span>{tier}</span>
                        {billingTier === tier && <span className="w-1.5 h-1.5 rounded-full bg-primary" />}
                      </div>
                    </button>
                  ))}
                </div>
                <span className={HELP_CLS}>{TIER_DESC[billingTier]}</span>
              </div>
            </div>

            {err && (
              <div className="px-5 py-3">
                <p className="text-xs text-danger-soft-fg bg-danger-soft border border-danger/20 rounded-md px-3 py-2">{err}</p>
              </div>
            )}

            <div className="flex justify-end gap-2 px-5 py-3.5 bg-surface-1/40">
              <Link to="/tenants"><Button type="button" variant="ghost">Cancelar</Button></Link>
              <Button type="submit" disabled={submitting}>
                <Sparkles size={14} />
                {submitting ? "Onboarding…" : "Criar tenant"}
              </Button>
            </div>
          </form>

          {/* Sidebar de info */}
          <aside className="space-y-3">
            <div className="rounded-md border border-border bg-surface p-4">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-3">Saga steps</p>
              <ol className="space-y-1 text-xs">
                {SAGA_STEPS.map((step, i) => (
                  <li key={step} className="flex items-center gap-2.5 py-0.5">
                    <code className="font-mono-tight text-muted-foreground/70 w-5 shrink-0 text-right">{i}</code>
                    <code className="font-mono-tight text-foreground/90">{step}</code>
                  </li>
                ))}
              </ol>
              <p className="text-[11px] text-muted-foreground mt-3 pt-3 border-t border-border-subtle leading-snug">
                Falha em qualquer step dispara <strong className="font-medium text-foreground/80">compensation reversa</strong> automática nos steps já concluídos.
              </p>
            </div>

            <div className="rounded-md border border-warning/30 bg-warning-soft/50 p-3 text-xs flex gap-2">
              <Info size={14} className="shrink-0 mt-0.5 text-warning-soft-fg" />
              <div className="leading-snug">
                <p className="font-medium text-warning-soft-fg">Stubs in-memory</p>
                <p className="text-warning-soft-fg/85 mt-0.5">
                  Wynk IDP / DB / DNS / Storage / Branding / Notifications ainda estão em desenvolvimento. Os clients downstream registram intenção mas não acionam serviços reais.
                </p>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}
